/**
 * Provides a set of methods to compute swiss social security numbers' checksums
 * and check their validity.
 */
export class AHV13 {
    /**
     * Compute the checksum for a given social security number.
     *
     * @param ahv12 - Social security number **without its checksum** (aka AHV12)
     * @returns The social security number's checksum
     */
    calculateCheckSum(ahv12) {
        let totalChecksum = 0;
        for (let i = 0; i < ahv12.length; i++) {
            const number = ahv12[i];
            if (i % 2 === 0) {
                totalChecksum = totalChecksum + 13 * number;
            }
            else {
                totalChecksum = totalChecksum + number;
            }
        }
        const nextTimesTen = Math.ceil(totalChecksum / 10) * 10;
        return nextTimesTen - totalChecksum;
    }
    /**
     * Remove the '.' characters and reverse the social security number
     *
     * @param arr - Social security number
     * @returns The social security number **reversed and in the form of an
     * array**
     */
    preProcessArray(arr) {
        return arr
            .split('.')
            .join('')
            .split('')
            .reverse()
            .map((number) => parseInt(number));
    }
    /**
     * Parse the social security number and return its checksum
     *
     * # Example
     *
     * ```ts
     * const validator = new AHV13();
     * const checksum = validator.checkSum('756.9217.0769.8');
     *
     * console.log(checksum); // 5
     * ```
     *
     * @param ahvNumber12 - Social security number **without its checksum**
     * @returns The social security number's checksum
     */
    checkSum(ahvNumber12) {
        const parsedNumber = this.preProcessArray(ahvNumber12);
        return this.calculateCheckSum(parsedNumber);
    }
    /**
     * Validate a AHV13 Number with or without dots in the form of '756.9217.0769.85'
     *
     * # Example
     *
     * ```ts
     * const validator = new AHV13();
     *
     * console.log(validator.isValid("756.9217.0769.85")) // true
     * console.log(validator.isValid("756.9217.0769.82")) // false
     * ```
     *
     * @param ahv13 -  Social security number **with its checksum**
     * @returns `true` if the number is valid. `false` otherwise.
     */
    isValid(ahv13) {
        const reversedSSNArray = this.preProcessArray(ahv13);
        // enforce length of 13
        if (reversedSSNArray.length !== 13) {
            return false;
        }
        // remove the first entry, as it is the checksum
        const checkSumSSN = reversedSSNArray.shift();
        const checkSumCalculated = this.calculateCheckSum(reversedSSNArray);
        return checkSumCalculated === checkSumSSN;
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiYWh2MTMuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi9zcmMvbGliL2FodjEzLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBOzs7R0FHRztBQUNILE1BQU0sT0FBTyxLQUFLO0lBQ2hCOzs7OztPQUtHO0lBQ0ssaUJBQWlCLENBQUMsS0FBZTtRQUN2QyxJQUFJLGFBQWEsR0FBRyxDQUFDLENBQUM7UUFDdEIsS0FBSyxJQUFJLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQyxHQUFHLEtBQUssQ0FBQyxNQUFNLEVBQUUsQ0FBQyxFQUFFLEVBQUU7WUFDckMsTUFBTSxNQUFNLEdBQUcsS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDO1lBQ3hCLElBQUksQ0FBQyxHQUFHLENBQUMsS0FBSyxDQUFDLEVBQUU7Z0JBQ2YsYUFBYSxHQUFHLGFBQWEsR0FBRyxFQUFFLEdBQUcsTUFBTSxDQUFDO2FBQzdDO2lCQUFNO2dCQUNMLGFBQWEsR0FBRyxhQUFhLEdBQUcsTUFBTSxDQUFDO2FBQ3hDO1NBQ0Y7UUFDRCxNQUFNLFlBQVksR0FBRyxJQUFJLENBQUMsSUFBSSxDQUFDLGFBQWEsR0FBRyxFQUFFLENBQUMsR0FBRyxFQUFFLENBQUM7UUFDeEQsT0FBTyxZQUFZLEdBQUcsYUFBYSxDQUFDO0lBQ3RDLENBQUM7SUFFRDs7Ozs7O09BTUc7SUFDSyxlQUFlLENBQUMsR0FBVztRQUNqQyxPQUFPLEdBQUc7YUFDUCxLQUFLLENBQUMsR0FBRyxDQUFDO2FBQ1YsSUFBSSxDQUFDLEVBQUUsQ0FBQzthQUNSLEtBQUssQ0FBQyxFQUFFLENBQUM7YUFDVCxPQUFPLEVBQUU7YUFDVCxHQUFHLENBQUMsQ0FBQyxNQUFNLEVBQUUsRUFBRSxDQUFDLFFBQVEsQ0FBQyxNQUFNLENBQUMsQ0FBQyxDQUFDO0lBQ3ZDLENBQUM7SUFFRDs7Ozs7Ozs7Ozs7Ozs7T0FjRztJQUNILFFBQVEsQ0FBQyxXQUFtQjtRQUMxQixNQUFNLFlBQVksR0FBRyxJQUFJLENBQUMsZUFBZSxDQUFDLFdBQVcsQ0FBQyxDQUFDO1FBQ3ZELE9BQU8sSUFBSSxDQUFDLGlCQUFpQixDQUFDLFlBQVksQ0FBQyxDQUFDO0lBQzlDLENBQUM7SUFFRDs7Ozs7Ozs7Ozs7Ozs7T0FjRztJQUNILE9BQU8sQ0FBQyxLQUFhO1FBQ25CLE1BQU0sZ0JBQWdCLEdBQUcsSUFBSSxDQUFDLGVBQWUsQ0FBQyxLQUFLLENBQUMsQ0FBQztRQUVyRCx1QkFBdUI7UUFDdkIsSUFBSSxnQkFBZ0IsQ0FBQyxNQUFNLEtBQUssRUFBRSxFQUFFO1lBQ2xDLE9BQU8sS0FBSyxDQUFDO1NBQ2Q7UUFFRCxnREFBZ0Q7UUFDaEQsTUFBTSxXQUFXLEdBQUcsZ0JBQWdCLENBQUMsS0FBSyxFQUFFLENBQUM7UUFDN0MsTUFBTSxrQkFBa0IsR0FBRyxJQUFJLENBQUMsaUJBQWlCLENBQUMsZ0JBQWdCLENBQUMsQ0FBQztRQUNwRSxPQUFPLGtCQUFrQixLQUFLLFdBQVcsQ0FBQztJQUM1QyxDQUFDO0NBQ0YifQ==